import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  ListItemIcon,
  Paper,
} from '@mui/material';
import { CloudUpload, TableChart, Science, Send, ArrowBack } from '@mui/icons-material';
import Plot from 'react-plotly.js';
import { useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useData } from '../contexts/DataContext';
import MainLayout from '../components/Layout/MainLayout';

const parsePlotJson = (plotStr) => {
  if (!plotStr) return null;
  try {
    return typeof plotStr === 'string' ? JSON.parse(plotStr) : plotStr;
  } catch {
    return null;
  }
};

const Dataset = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { sensors, fetchSensors } = useProject();
  const { plotCSV, saveToPark, fetchSamples, samples } = useData();
  const [selectedSensor, setSelectedSensor] = useState('');
  const [uploading, setUploading] = useState(false);

  // Review mode states
  const [reviewMode, setReviewMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [plotData, setPlotData] = useState(null);
  const [sampleNames, setSampleNames] = useState([]);
  const [samplesToRemove, setSamplesToRemove] = useState([]);
  const [loadingPlots, setLoadingPlots] = useState(false);

  // Refs for plot click handling
  const sampleNamesRef = useRef(sampleNames);
  sampleNamesRef.current = sampleNames;

  useEffect(() => {
    if (projectId) {
      fetchSensors(projectId);
      fetchSamples(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0 && selectedSensor) {
      setSelectedFiles(files);
      setReviewMode(false);
      setPlotData(null);
      setSampleNames([]);
      setSamplesToRemove([]);
    }
  };

  const handleReviewData = async () => {
    if (selectedFiles.length === 0 || !selectedSensor) return;

    setLoadingPlots(true);
    try {
      const response = await plotCSV(selectedSensor, selectedFiles);
      setPlotData(response);

      const names = await extractSampleNamesFromFiles(selectedFiles);
      setSampleNames(names);
      setReviewMode(true);
    } catch (error) {
      console.error('Failed to generate plots:', error);
    } finally {
      setLoadingPlots(false);
    }
  };

  const extractSampleNamesFromFiles = async (files) => {
    const allNames = [];

    for (const file of files) {
      const text = await file.text();
      const lines = text.split('\n');

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line) {
          const parts = line.split(/[,;]/);
          if (parts[0]) {
            allNames.push(parts[0].trim());
          }
        }
      }
    }

    return allNames;
  };

  const toggleSampleRemoval = (sampleName) => {
    if (!sampleName) return;
    setSamplesToRemove(prev => {
      if (prev.includes(sampleName)) {
        return prev.filter(name => name !== sampleName);
      } else {
        return [...prev, sampleName];
      }
    });
  };

  // Ref-based click handler so the Plotly callback always reads latest state
  const toggleRef = useRef(toggleSampleRemoval);
  toggleRef.current = toggleSampleRemoval;

  const handlePlotClick = useRef((event) => {
    if (!event || !event.points || event.points.length === 0) return;
    const point = event.points[0];
    const candidates = [point.data?.name, point.text, point.x];
    const names = sampleNamesRef.current;
    const name = candidates.find(c => c && names.includes(String(c)));
    if (name) {
      toggleRef.current(String(name));
    }
  }).current;

  // Manually attach plotly_click to the graph div after every render.
  // react-plotly.js v2 + plotly.js v3 has a bug where syncEventHandlers
  // skips re-attachment when the handler reference is unchanged, but
  // Plotly.react() in v3 clears existing listeners.
  const attachClickHandler = (graphDiv) => {
    if (!graphDiv) return;
    graphDiv.removeAllListeners('plotly_click');
    graphDiv.on('plotly_click', handlePlotClick);
  };

  // Auto-recalculate plots when samples are toggled (debounced)
  const recalcTimerRef = useRef(null);
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (!reviewMode || selectedFiles.length === 0 || !selectedSensor) return;

    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);

    recalcTimerRef.current = setTimeout(async () => {
      setLoadingPlots(true);
      try {
        const response = await plotCSV(selectedSensor, selectedFiles, samplesToRemove);
        setPlotData(response);
      } catch (error) {
        console.error('Failed to recalculate plots:', error);
      } finally {
        setLoadingPlots(false);
      }
    }, 800);

    return () => {
      if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samplesToRemove]);

  const handleSendToPark = async () => {
    if (selectedFiles.length === 0 || !selectedSensor) return;

    setUploading(true);
    try {
      await saveToPark(selectedSensor, selectedFiles, samplesToRemove);
      await fetchSamples(projectId);

      setReviewMode(false);
      setSelectedFiles([]);
      setPlotData(null);
      setSampleNames([]);
      setSamplesToRemove([]);
    } catch (error) {
      console.error('Failed to send to Park:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleBackToUpload = () => {
    setReviewMode(false);
    setSelectedFiles([]);
    setPlotData(null);
    setSampleNames([]);
    setSamplesToRemove([]);
  };

  // Pre-parse plot data to avoid IIFEs in JSX
  const parsedSpectral = useMemo(() => parsePlotJson(plotData?.chart_html), [plotData?.chart_html]);
  const parsedQT = useMemo(() => parsePlotJson(plotData?.chart_qt_html), [plotData?.chart_qt_html]);
  const parsedMahalanobis = useMemo(() => parsePlotJson(plotData?.chart_mahanalobis_html), [plotData?.chart_mahanalobis_html]);

  const plotLayout = (parsed) => ({
    ...parsed?.layout,
    autosize: true,
    width: undefined,
    height: 500,
    showlegend: false,
    clickmode: 'event',
    hovermode: 'closest',
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
    margin: { l: 60, r: 30, t: 40, b: 60 },
  });

  const plotConfig = { responsive: true, displayModeBar: true, displaylogo: false };
  const plotStyle = { width: '100%', height: 500 };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Dataset Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Import and manage your spectroscopy data
          </Typography>
        </Box>

        {!reviewMode ? (
          <>
            {/* Upload Section */}
            <Card sx={{ mb: 4, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Upload Data
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select a sensor and upload CSV or DB files containing spectroscopy measurements
                </Typography>

                <Grid container spacing={3} alignItems="flex-end">
                  <Grid item xs={12} md={5}>
                    <TextField
                      select
                      fullWidth
                      label="Select Sensor"
                      value={selectedSensor}
                      onChange={(e) => setSelectedSensor(e.target.value)}
                      disabled={sensors.length === 0}
                    >
                      {sensors.map((sensor) => (
                        <MenuItem key={sensor.id} value={sensor.id}>
                          {sensor.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      component="label"
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<CloudUpload />}
                      disabled={!selectedSensor}
                      sx={{ py: 1.8 }}
                    >
                      Select Files
                      <input
                        type="file"
                        accept=".csv,.db"
                        multiple
                        hidden
                        onChange={handleFileSelection}
                      />
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<Science />}
                      disabled={!selectedSensor || selectedFiles.length === 0 || loadingPlots}
                      onClick={handleReviewData}
                      sx={{ py: 1.8 }}
                    >
                      {loadingPlots ? 'Loading...' : 'Review Data'}
                    </Button>
                  </Grid>
                </Grid>

                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Selected: {selectedFiles.map(f => f.name).join(', ')}
                    </Typography>
                  </Box>
                )}

                {sensors.length === 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                    }}
                  >
                    <Typography variant="body2" color="warning.dark">
                      No sensors configured. Please configure sensors in the Settings page first.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Data Overview */}
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <TableChart sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="h5" fontWeight={600}>
                      Data Overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {samples.length} total sample{samples.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>

                {samples.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 6,
                      px: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Data Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select files and click Review Data to preview before importing
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" paragraph>
                      Your dataset contains {samples.length} samples. Use the Editor page to label and
                      curate your data for model training.
                    </Typography>
                    <Grid container spacing={2}>
                      {['Labeled', 'Parked', 'Cortex'].map((status) => {
                        const count = samples.filter((s) => s.status === status).length;
                        return (
                          <Grid item xs={12} sm={4} key={status}>
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                              }}
                            >
                              <Typography variant="h4" fontWeight={700} color="primary">
                                {count}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {status} Samples
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Review Mode */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToUpload}
                variant="outlined"
              >
                Back to Upload
              </Button>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {loadingPlots && (
                  <Typography variant="body2" color="text.secondary">
                    Recalculating...
                  </Typography>
                )}
                <Button
                  startIcon={<Send />}
                  onClick={handleSendToPark}
                  variant="contained"
                  size="large"
                  disabled={uploading || loadingPlots}
                  color="primary"
                >
                  {uploading ? 'Sending...' : 'Send to Park'}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Plots - takes remaining space */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Card sx={{ mb: 3, borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Spectral Data
                    </Typography>
                    {parsedSpectral && (
                      <Plot
                        data={parsedSpectral.data}
                        layout={plotLayout(parsedSpectral)}
                        useResizeHandler
                        onInitialized={(figure, graphDiv) => attachClickHandler(graphDiv)}
                        onUpdate={(figure, graphDiv) => attachClickHandler(graphDiv)}
                        config={plotConfig}
                        style={plotStyle}
                      />
                    )}
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3, borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      QT Plot (Quality Control)
                    </Typography>
                    {parsedQT && (
                      <Plot
                        data={parsedQT.data}
                        layout={plotLayout(parsedQT)}
                        useResizeHandler
                        onInitialized={(figure, graphDiv) => attachClickHandler(graphDiv)}
                        onUpdate={(figure, graphDiv) => attachClickHandler(graphDiv)}
                        config={plotConfig}
                        style={plotStyle}
                      />
                    )}
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3, borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Mahalanobis Distance
                    </Typography>
                    {parsedMahalanobis && (
                      <Plot
                        data={parsedMahalanobis.data}
                        layout={plotLayout(parsedMahalanobis)}
                        useResizeHandler
                        onInitialized={(figure, graphDiv) => attachClickHandler(graphDiv)}
                        onUpdate={(figure, graphDiv) => attachClickHandler(graphDiv)}
                        config={plotConfig}
                        style={plotStyle}
                      />
                    )}
                  </CardContent>
                </Card>
              </Box>

              {/* Right sidebar - Sample List */}
              <Card sx={{ width: 240, flexShrink: 0, borderRadius: 3, alignSelf: 'flex-start', position: 'sticky', top: 20 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Samples ({sampleNames.length - samplesToRemove.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {samplesToRemove.length > 0 ? `${samplesToRemove.length} removed` : 'Click samples or plot traces'}
                  </Typography>
                  <Paper
                    sx={{
                      maxHeight: 'calc(100vh - 220px)',
                      overflow: 'auto',
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <List dense>
                      {/* Active samples first */}
                      {sampleNames.filter(n => !samplesToRemove.includes(n)).map((sampleName) => (
                        <ListItemButton
                          key={sampleName}
                          onClick={() => toggleSampleRemoval(sampleName)}
                          sx={{ py: 0.25 }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <Checkbox
                              edge="start"
                              checked={false}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                              sx={{ pointerEvents: 'none' }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={sampleName}
                            sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                          />
                        </ListItemButton>
                      ))}
                      {/* Removed samples at the bottom */}
                      {samplesToRemove.length > 0 && (
                        <>
                          <ListItem sx={{ py: 0.5 }}>
                            <Typography variant="caption" color="error" fontWeight={600}>
                              Removed ({samplesToRemove.length})
                            </Typography>
                          </ListItem>
                          {samplesToRemove.map((sampleName) => (
                            <ListItemButton
                              key={sampleName}
                              onClick={() => toggleSampleRemoval(sampleName)}
                              sx={{
                                py: 0.25,
                                borderLeft: `3px solid ${theme.palette.error.main}`,
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <Checkbox
                                  edge="start"
                                  checked={true}
                                  tabIndex={-1}
                                  disableRipple
                                  size="small"
                                  color="error"
                                  sx={{ pointerEvents: 'none' }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={sampleName}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    fontSize: '0.8rem',
                                    textDecoration: 'line-through',
                                    color: theme.palette.text.disabled,
                                  },
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </>
                      )}
                    </List>
                  </Paper>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Container>
    </MainLayout>
  );
};

export default Dataset;
